import React from 'react';
import { FaUsers, FaShoppingCart, FaGamepad, FaStar } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import AnimatedCounter from '../common/AnimatedCounter';
import ScrollReveal from '../common/ScrollReveal';

const StatsSection = React.memo(({ stats, gamesCount }) => {
    const { t } = useLanguage();
    const statItems = [
        { icon: FaUsers, rawValue: stats.users, suffix: '+', label: t('stats.totalMembers'), desc: t('stats.realUsers') },
        { icon: FaShoppingCart, rawValue: stats.orders, suffix: '+', label: t('stats.completedOrders'), desc: t('stats.successfulTx') },
        { icon: FaGamepad, rawValue: gamesCount, suffix: '+', label: t('stats.supportedGames'), desc: t('stats.popularGames') },
        { icon: FaStar, rawValue: 4.9, suffix: '/5', label: t('stats.satisfaction'), desc: t('stats.fromRealReviews'), decimals: 1 },
    ];

    return (
        <section className="py-0 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto -mt-2">
                <ScrollReveal animation="scaleUp" duration={800}>
                    <div className="rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E3A8A 50%, #1D4ED8 100%)' }}>
                        <div className="px-8 py-10 md:py-14 relative overflow-hidden">
                            {/* Decorative Elements */}
                            <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, white 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
                            <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, white 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />

                            <div className="text-center mb-8 relative z-10">
                                <h2 className="text-2xl md:text-3xl font-black text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                    {t('stats.whyChoose')} <span style={{ background: 'linear-gradient(90deg, #60A5FA, #93C5FD)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Fastlane</span><span style={{ background: 'linear-gradient(90deg, #EF4444, #F87171)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Lv.</span>
                                </h2>
                                <p className="text-blue-300 text-sm">{t('stats.trustedPlatform')}</p>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 relative z-10">
                                {statItems.map((stat, i) => (
                                    <ScrollReveal key={i} animation="fadeUp" delay={i * 120} duration={600}>
                                        <div className="text-center rounded-2xl p-5 transition-all duration-300 hover:scale-105 hover:bg-white/15" style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                            <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
                                                <stat.icon className="text-xl text-white" />
                                            </div>
                                            <div className="text-2xl md:text-3xl font-black text-white mb-0.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                                <AnimatedCounter
                                                    value={stat.rawValue}
                                                    suffix={stat.suffix}
                                                    duration={2000}
                                                    decimals={stat.decimals || 0}
                                                />
                                            </div>
                                            <div className="text-xs text-blue-200 font-semibold mb-0.5">{stat.label}</div>
                                            <div className="text-[10px] text-blue-300/60">{stat.desc}</div>
                                        </div>
                                    </ScrollReveal>
                                ))}
                            </div>
                        </div>
                    </div>
                </ScrollReveal>
            </div>
        </section>
    );
});

StatsSection.displayName = 'StatsSection';

export default StatsSection;
